import React, { useRef, useState } from 'react'
import { CloseButton, IconUpload, Overlay, Pill, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { importContactsFile, type ImportPreview } from './crmApi'

const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  company: 'Company',
  phone: 'Phone',
  title: 'Title',
}

/**
 * CSV/Excel upload → server dry-run preview (detected column mapping + sample +
 * counts) → confirm → import. Shared by Opportunities (into a group) and Leads
 * (straight to Cold Leads). Calls `onImported` after a successful commit so the
 * caller can refresh its data.
 */
export const ImportButton: React.FC<{
  groupId?: string
  onImported: () => void
  label?: string
  variant?: 'primary' | 'secondary'
  size?: 'sm' | 'md'
}> = ({ groupId, onImported, label = 'Upload CSV / Excel', variant = 'secondary', size = 'sm' }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<(ImportPreview & { _file: File }) | null>(null)
  const [busy, setBusy] = useState(false)

  const pick = () => inputRef.current?.click()

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    setBusy(true)
    try {
      const r = (await importContactsFile(file, { groupId, dryRun: true })) as ImportPreview
      if (!r.willImport) crmToast('No importable rows found — needs a name or email column', 'error')
      else setPreview({ ...r, _file: file })
    } catch (err) {
      crmToast((err as Error).message || 'Could not read file', 'error')
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    if (!preview) return
    setBusy(true)
    try {
      const r: any = await importContactsFile(preview._file, { groupId, dryRun: false })
      crmToast(
        `Imported ${r.created} lead${r.created === 1 ? '' : 's'}${
          r.linked ? ` · ${r.linked} linked to existing compan${r.linked === 1 ? 'y' : 'ies'}` : ''
        }${r.duplicates ? ` · ${r.duplicates} duplicate${r.duplicates === 1 ? '' : 's'} skipped` : ''}`,
        'success',
      )
      setPreview(null)
      onImported()
    } catch (err) {
      crmToast((err as Error).message || 'Import failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={onFile}
      />
      <Button size={size} variant={variant} loading={busy && !preview} onClick={pick}>
        <IconUpload className="w-3.5 h-3.5" /> {label}
      </Button>

      {preview && (
        <Overlay onClose={() => (busy ? null : setPreview(null))} width={640}>
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
            <div>
              <p className="font-semibold text-txt-primary">Confirm import</p>
              <p className="text-xs text-txt-tertiary mt-0.5">
                {preview.willImport} contact{preview.willImport === 1 ? '' : 's'} will be added as cold leads
                {preview.skipped ? ` · ${preview.skipped} blank row${preview.skipped === 1 ? '' : 's'} skipped` : ''}
              </p>
            </div>
            <CloseButton onClick={() => (busy ? null : setPreview(null))} />
          </div>
          <div className="px-5 py-4 overflow-auto space-y-4">
            {/* Detected columns */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-txt-tertiary font-medium mb-1.5">Detected columns</p>
              {Object.keys(preview.mapping).length === 0 ? (
                <p className="text-sm text-red-600">No columns recognised — the file needs a header row.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(preview.mapping).map(([header, field]) => (
                    <span
                      key={header}
                      className="inline-flex items-center gap-1 text-[11px] rounded-md border border-border bg-surface-secondary px-2 py-1"
                    >
                      <span className="text-txt-secondary">{header}</span>
                      <span className="text-txt-tertiary">→</span>
                      <span className="font-semibold text-brand-600">{FIELD_LABEL[field] || field}</span>
                    </span>
                  ))}
                </div>
              )}
              {preview.headers.filter((h) => !preview.mapping[h]).length > 0 && (
                <p className="text-[11px] text-txt-tertiary mt-1.5">
                  Ignored: {preview.headers.filter((h) => !preview.mapping[h]).join(', ')}
                </p>
              )}
            </div>

            {/* Sample */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-txt-tertiary font-medium mb-1.5">
                Preview (first {preview.sample.length})
              </p>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary bg-surface-secondary">
                      <th className="py-1.5 px-3 font-medium">Name</th>
                      <th className="py-1.5 px-3 font-medium">Company</th>
                      <th className="py-1.5 px-3 font-medium">Email</th>
                      <th className="py-1.5 px-3 font-medium">Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((s, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-1.5 px-3 text-txt-primary">{s.name}</td>
                        <td className="py-1.5 px-3 text-txt-secondary">{s.company || '—'}</td>
                        <td className="py-1.5 px-3 text-brand-600">{s.email || '—'}</td>
                        <td className="py-1.5 px-3 text-txt-secondary">{s.phone || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {groupId ? (
              <Pill tone="blue">Adds to this group and the Board</Pill>
            ) : (
              <Pill tone="blue">Adds straight to Cold Leads on the Board</Pill>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => setPreview(null)}>
              Cancel
            </Button>
            <Button size="sm" loading={busy} disabled={!preview.willImport} onClick={confirm}>
              Import {preview.willImport} as cold leads
            </Button>
          </div>
        </Overlay>
      )}
    </>
  )
}
